import { useMemo } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

type FaqItem = { q: string; a: string };

const MENTORIA_FAQS_ES: FaqItem[] = [
  {
    q: "¿Qué diferencia real hay entre los planes Quincenal, Semanal e Intensivo?",
    a: "La diferencia principal es el nivel de implicación y la velocidad a la que quieres ir. Quincenal (2h/mes): Ideal si el caos te come y necesitas que te ayudemos a poner orden básico en tus números y operativa. Semanal (4h/mes): Para negocios que ya funcionan, pero necesitan optimizar márgenes, revisar contratos y delegar para crecer sin romper nada. Intensivo (8h/mes + WhatsApp): Nos convertimos en tu Director de Operaciones externo. Implicación total en decisiones críticas y acceso directo para apagar fuegos diarios.",
  },
  {
    q: "¿Puedo contratar horas extra si un mes necesito más ayuda?",
    a: "Sí, por supuesto. Sabemos que en el retail hay momentos críticos (la apertura de un nuevo local, una crisis con el personal o una negociación dura con un proveedor). Si necesitas más apoyo puntual, puedes solicitar \"horas booster\" a tu mentor sin tener que subir de plan permanentemente.",
  },
  {
    q: "¿Qué pasa si un mes no consumo todas las horas de mi plan?",
    a: "Se acumulan para el mes siguiente. Entendemos perfectamente que hay temporadas de locura (como Navidad, Black Friday o temporada alta en hostelería) en las que apenas tienes tiempo de sentarte frente al ordenador. Si un mes no puedes hacer tus sesiones, esas horas se guardan en tu bolsa para cuando tengas más disponibilidad.",
  },
  {
    q: "¿Hay devolución del dinero por las horas que no utilice?",
    a: "No. Al asignarte un Mentor, estamos bloqueando un espacio exclusivo en su agenda mensual que no podemos ofrecer a otro cliente. Por eso, para proteger tu inversión, te permitimos acumular las horas no disfrutadas para el futuro, pero no realizamos reembolsos de la cuota mensual.",
  },
  {
    q: "¿Hay permanencia en la mentoría?",
    a: "Sin permanencia. Los mentores son un extra de pago, mes a mes, disponible desde el plan Avanzado (99 €/mes); verás su precio antes de pagar. Todos los planes (Básico 39 €, Club Pro 69 €, Avanzado 99 €/mes) son mensuales y se cancelan cuando quieras. Lite (0 €) no pide tarjeta.",
  },
  {
    q: "No tengo claro qué plan necesito, ¿puedo hablar con alguien antes de pagar?",
    a: "Sí, claro. De hecho, nos gusta conocerte antes de empezar. Puedes agendar una llamada rápida de 15 minutos sin compromiso con nuestro equipo. Nos cuentas en qué punto está tu negocio, qué te quita el sueño por las noches, y te diremos con total honestidad qué nivel de mentoría se adapta mejor a tu momento actual (o si de momento con el Club Pro te es suficiente).",
  },
];

const MENTORIA_FAQS_EN: FaqItem[] = [
  {
    q: "What's the real difference between the Biweekly, Weekly and Intensive plans?",
    a: "The main difference is the level of involvement and the pace you want to move at. Biweekly (2h/month): Ideal if chaos is eating you and you need help putting basic order in your numbers and operations. Weekly (4h/month): For businesses already running, but needing to optimize margins, review contracts and delegate to grow without breaking anything. Intensive (8h/month + WhatsApp): We become your external Operations Director. Full involvement in critical decisions and direct access to put out daily fires.",
  },
  {
    q: "Can I book extra hours if I need more help in a given month?",
    a: "Yes, of course. We know retail has critical moments (opening a new location, a staff crisis or a tough negotiation with a supplier). If you need more punctual support, you can request \"booster hours\" from your mentor without permanently moving up a tier.",
  },
  {
    q: "What happens if I don't use all the hours of my plan in a month?",
    a: "They roll over to the following month. We totally get that there are crazy seasons (Christmas, Black Friday or high season in hospitality) when you barely have time to sit in front of the computer. If you can't do your sessions in a given month, those hours are saved in your bank for when you have more availability.",
  },
  {
    q: "Is there a refund for unused hours?",
    a: "No. By assigning you a Mentor, we are blocking an exclusive slot in their monthly schedule that we cannot offer to another client. That's why, to protect your investment, we let you roll over unused hours for the future, but we don't refund the monthly fee.",
  },
  {
    q: "Is there a minimum term for mentoring?",
    a: "No commitment. Mentors are a paid, month-to-month extra available from the Advanced plan (€99/month); you'll see the price before paying. All plans (Basic €39, Club Pro €69, Advanced €99/month) are monthly and can be cancelled anytime. Lite (€0) needs no card.",
  },
  {
    q: "I'm not sure which plan I need, can I talk to someone before paying?",
    a: "Yes, sure. In fact, we like to get to know you before starting. You can book a quick 15-minute no-commitment call with our team. Tell us where your business stands, what's keeping you up at night, and we'll tell you honestly which mentoring level best fits your current moment (or whether the Club Pro alone is enough for now).",
  },
];

/** Source of truth for Mentoria FAQ — exported so the parent page (Mentoria.tsx)
 *  can build the FAQPage JSON-LD via Helmet (SSR shells include it). */
export const getMentoriaFaqs = (lang: "es" | "en"): FaqItem[] =>
  lang === "es" ? MENTORIA_FAQS_ES : MENTORIA_FAQS_EN;

const MentoriaFAQ = () => {
  const { lang } = useLanguage();
  const es = lang === "es";
  const faqs = useMemo(() => getMentoriaFaqs(lang), [lang]);

  return (
    <section className="py-20 bg-card border-t border-border" id="faq-mentoria">
      <section className="container max-w-3xl mx-auto px-4">
        <h2 className="font-body text-3xl md:text-4xl font-bold text-foreground text-center mb-10">
          {es ? "Preguntas frecuentes sobre Tu Mentor" : "Your Mentor FAQs"}
        </h2>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((faq, i) => (
            <AccordionItem
              key={i}
              value={`mentoria-faq-${i}`}
              className="bg-background rounded-xl border border-border px-6"
            >
              <AccordionTrigger className="font-body text-lg font-semibold text-foreground hover:no-underline text-left">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="font-body text-muted-foreground leading-relaxed pb-4">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </section>
  );
};

export default MentoriaFAQ;
